/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t, Trans } from '@lingui/macro'
import AddIcon from '@mui/icons-material/Add'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import BarChartIcon from '@mui/icons-material/BarChart'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import { Box, Button, CircularProgress, IconButton, styled } from '@mui/material'
import Typography from '@mui/material/Typography'
import { FalconPaginationMeta, OrganizationMetadataDTO } from '@om1/falcon-api'
import { AssistantConversationCreateDTO } from '@om1/falcon-api/codegen/client/models/AssistantConversationCreateDTO'
import { DataDeliveryAdminOrganizationMetadataService } from '@om1/falcon-api/codegen/client/services/DataDeliveryAdminOrganizationMetadataService'
import { ExplorerCohortsService } from '@om1/falcon-api/codegen/client/services/ExplorerCohortsService'
import { explorerAssistantConversationActions } from '@om1/platform-assistant-ui'
import { useUserPermissions } from '@om1/platform-authentication'
import { LightTooltip } from '@om1/platform-components'
import { Routes, toPath } from '@om1/platform-routing'
import { trackingActions, TrackingPlanEvents } from '@om1/platform-tracking'
import { useSnackbar } from 'notistack'

import { PlatformPermissions, RoutedFrameworkComponentProps } from '@om1/platform-utils'
import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { createGenerateReportComponent } from '../../components/buttons/GenerateReport'
import { createCreateCohortWizardDialogComponent } from '../../components/create/CreateCohortWizardDialog'
import { sortByCohortField } from '../../components/list/utils/cohort-list-utils'
import { CohortSaveValues } from '../../components/shared/CreateEditCohortComponent'
import { CohortRoutes } from '../../routes'
import {
    cohortCommonActions,
    cohortCreateActions,
    cohortDeleteActions,
    cohortEditActions,
    cohortListActions,
    CohortListItem,
    CohortLoading,
    CohortTrackingValues,
    ReportAnalyticsType,
    reportsActions
} from '../../state'
import { CohortDashboardCohortCard } from './CohortDashboardCohortCard'
import { DashboardCohortList } from './DashboardCohortList'
import { DashboardCohortReports } from './DashboardCohortReports'

const WizardDialog = createCreateCohortWizardDialogComponent()

export type CohortDashboardPageComponentProps = RoutedFrameworkComponentProps<
    { reportAnalyticsType: ReportAnalyticsType; search?: string; collapsed?: boolean },
    { cohortOwner: DashboardCohortOwner; cohortId: string; reportType: ReportAnalyticsType },
    {
        cohorts: CohortListItem[]
        datasets: CohortListItem[]
        paginationMeta: FalconPaginationMeta
        createLoading: boolean
        cohortsLoading: boolean
        cohortSizeLoading: CohortLoading
        cohortAttritionFunnelLoading: boolean
        conversations: any[]
    },
    typeof cohortListActions &
        typeof trackingActions &
        typeof reportsActions &
        typeof cohortCreateActions &
        typeof cohortEditActions &
        Pick<typeof cohortDeleteActions, 'cohortDelete'> &
        Pick<typeof cohortCommonActions, 'cohortSizeGet'> &
        Pick<typeof trackingActions, 'trackEvent'> &
        typeof explorerAssistantConversationActions,
    {}
>

export enum DashboardCohortOwner {
    CUSTOM = 'custom',
    DATASET = 'dataset'
}

export const CohortDashboardPageComponent: FunctionComponent<CohortDashboardPageComponentProps> = ({ state, actions, routing }) => {
    const { permissions, decodedToken } = useUserPermissions()
    const permissionsLoading = !decodedToken
    const { enqueueSnackbar } = useSnackbar()

    const history = useHistory()
    const location = useLocation()
    const GenerateReportComponent = createGenerateReportComponent()
    const [createModalVisible, setCreateModalVisible] = useState(false)
    const [cohortOwner, setCohortOwner] = useState<DashboardCohortOwner | undefined>(routing.pathParams.cohortOwner)
    const [cohortId, setCohortId] = useState<string | undefined>(routing.pathParams.cohortId)
    const [page, setPage] = useState(1)

    // Organization settings for hide system cohorts feature
    const [organizationSettings, setOrganizationSettings] = useState<OrganizationMetadataDTO | null>(null)

    // Fetch organization settings on mount
    useEffect(() => {
        DataDeliveryAdminOrganizationMetadataService.getCurrentOrgDataDeliveryOrganizationMetadataOrgidGet()
            .then((metadata) => {
                setOrganizationSettings(metadata)
            })
            .catch(() => {
                // Silently fail - org settings may not exist
                setOrganizationSettings(null)
            })
    }, [])

    // Get all cohorts from both User and System lists
    const allCohorts = useMemo(() => {
        const userCohorts = state.cohorts || []
        const systemCohorts = state.datasets || []
        return [...userCohorts, ...systemCohorts]
    }, [state.cohorts, state.datasets])

    // Helper to check if a cohort name contains "Network"
    const isNetworkCohort = useCallback((name: string | undefined) => {
        return (name || '').toLowerCase().includes('network')
    }, [])

    // Helper to check if a cohort is Real World Data Cloud
    const isRWDC = useCallback((name: string | undefined) => {
        return name === 'OM1 Real World Data Cloud'
    }, [])

    // Filter cohorts for system/datasets list (isSystem=true OR isFeatured=true)
    // When hideSystemCohorts is enabled, only show Real World Data Cloud, Network cohorts, and featured cohorts
    const sortedDatasets = useMemo(() => {
        const hideSystemCohorts = organizationSettings?.hideSystemCohorts ?? false
        let datasets = allCohorts.filter((cohort) => cohort?.isSystem === true || cohort?.isFeatured === true)

        // If hide system cohorts is enabled, only show Real World Data Cloud, Network cohorts, and featured cohorts
        if (hideSystemCohorts) {
            datasets = datasets.filter((cohort) => isRWDC(cohort?.name) || isNetworkCohort(cohort?.name) || cohort?.isFeatured === true)
        }

        // Prioritize OM1 Real World Data Cloud
        return datasets.sort((a, b) => {
            const aIsRWDC = isRWDC(a?.name)
            const bIsRWDC = isRWDC(b?.name)

            if (aIsRWDC && !bIsRWDC) return -1
            if (!aIsRWDC && bIsRWDC) return 1

            // For all other datasets, sort alphabetically by name
            return a?.name?.localeCompare(b?.name || '') || 0
        })
    }, [allCohorts, organizationSettings?.hideSystemCohorts, isRWDC, isNetworkCohort])

    const selectedCohort = useMemo(() => {
        if (cohortOwner === DashboardCohortOwner.DATASET) {
            return sortedDatasets.find((cohort) => cohort?.id === cohortId)
        } else {
            // For custom cohorts, we need to find in all cohorts that match our filter
            const customCohorts = allCohorts.filter((cohort) => cohort?.isSystem !== true && cohort?.isFeatured !== true)
            const stateCohort = customCohorts.find((cohort) => cohort?.id === cohortId)
            if (stateCohort) {
                return stateCohort
            } else if (state?.paginationMeta?.totalItems > 0 && cohortId && cohortId !== '-1') {
                // Load the specific cohort if not found in filtered lists
                actions.cohortsGetUserCohorts({
                    query: {},
                    path: { cohortId }
                })
            }
        }
    }, [cohortOwner, sortedDatasets, allCohorts, state?.paginationMeta?.totalItems, cohortId, actions])

    // Filter cohorts for custom list (isSystem=false AND isFeatured=false)
    const sortedCohorts = useMemo(() => {
        const customCohorts = allCohorts.filter((cohort) => cohort?.isSystem !== true && cohort?.isFeatured !== true)
        return [...customCohorts].sort(sortByCohortField('queryUpdatedDttm', 'desc'))
    }, [allCohorts])
    useEffect(() => {
        if (cohortId === '-1') {
            history.replace(`/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${DashboardCohortOwner.CUSTOM}/${location.search}`)
        }
    }, [cohortId, history, location.search])

    // Initial data loading on component mount
    useEffect(() => {
        // Load both system and user cohorts in parallel using the new ALL option
        const allCohortParams = {
            page: 1,
            pageSize: 50,
            searchValue: routing.queryParams.search
        }
        actions.cohortsGetAllCohorts({
            query: {
                USER: allCohortParams,
                SYSTEM: allCohortParams
            },
            path: {}
        })
    }, [actions, routing.queryParams.search])

    // Load additional pages for user cohorts based on pagination
    useEffect(() => {
        // Load additional user cohort pages when viewing custom cohorts
        if (cohortOwner === DashboardCohortOwner.CUSTOM && page > 1 && page <= state.paginationMeta.totalPages) {
            actions.cohortsGetUserCohorts({
                query: {
                    USER: { page, pageSize: 50, searchValue: routing.queryParams.search }
                },
                path: {}
            })
        }
    }, [actions, cohortOwner, page, routing.queryParams.search, state.paginationMeta.totalPages])

    // Load conversations for AI assistant
    useEffect(() => {
        if (!permissionsLoading && permissions?.includes(PlatformPermissions.ACCESS_ASSISTANT) && selectedCohort?.id) {
            actions.getConversations({ path: {}, query: { referenceCohortId: selectedCohort.id, page: 1, limit: 100 } })
        }
    }, [actions, selectedCohort?.id, permissions, permissionsLoading])

    useEffect(() => {
        if (cohortOwner === undefined) {
            history.replace(`/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${DashboardCohortOwner.DATASET}/${window.location.search}`)
            setCohortOwner(DashboardCohortOwner.DATASET)
        } else if (!cohortId && !state.cohortsLoading) {
            if (cohortOwner === DashboardCohortOwner.CUSTOM && sortedCohorts.length > 0) {
                history.replace(
                    `/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${DashboardCohortOwner.CUSTOM}/${sortedCohorts[0].id}/${location.search}`
                )
                setCohortId(sortedCohorts[0].id)
                setCohortOwner(DashboardCohortOwner.CUSTOM)
            } else if (sortedDatasets.length > 0 && cohortOwner === DashboardCohortOwner.DATASET) {
                history.replace(
                    `/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${DashboardCohortOwner.DATASET}/${sortedDatasets[0].id}/${location.search}`
                )
                setCohortId(sortedDatasets[0].id)
                setCohortOwner(DashboardCohortOwner.DATASET)
            }
        }
    }, [sortedCohorts, sortedDatasets, state.cohortsLoading, cohortId, cohortOwner, history, location.search])

    const [reportType, setReportType] = useState<ReportAnalyticsType>(routing.pathParams.reportType ?? ReportAnalyticsType.DEMOGRAPHICS)

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        routing.updateQuery({ search: event.target.value })
    }

    const handleEditCohort = useCallback(
        (payload: CohortTrackingValues) => {
            actions.trackEvent(TrackingPlanEvents.EDIT_COHORT, payload)
        },
        [actions]
    )

    const sizeLoading = state.cohortSizeLoading

    // Additional helper functions for new buttons
    const handleRefreshCount = useCallback(
        (cohortId: string, cohortName: string) => {
            actions.cohortSizeGet({ cohortId, cohortName })
        },
        [actions]
    )

    const handleCreateConversation = useCallback(() => {
        if (selectedCohort) {
            const newConversation: AssistantConversationCreateDTO = {
                referenceCohortId: selectedCohort.id
            }
            actions.launchNewConversation(newConversation, selectedCohort.query as any)
        }
    }, [selectedCohort, actions])

    const handleUpdateCohort = useCallback(
        (updatedCohort: CohortListItem) => {
            // Since we don't have a direct state update action for individual cohorts,
            // we can trigger a refresh of the cohorts to get the updated data
            // Alternatively, we could add a specific cohort update action to the state management

            // For now, let's refresh the data to get the updated cohort
            if (updatedCohort.isSystem) {
                // Refresh system cohorts
                actions.cohortsGetSystemCohorts({
                    query: {
                        SYSTEM: { page: 1, pageSize: 1000, searchValue: routing.queryParams.search }
                    },
                    path: {}
                })
            } else {
                // Refresh user cohorts
                actions.cohortsGetUserCohorts({
                    query: {
                        USER: { page: 1, pageSize: 100, searchValue: routing.queryParams.search }
                    },
                    path: {}
                })
            }
        },
        [actions, routing.queryParams.search]
    )

    // Button rendering functions
    const renderAttritionFunnelButton = useCallback(() => {
        if (!selectedCohort || selectedCohort.isSystem) return null
        const commonSx = {
            backgroundColor: '#D6E4F2',
            color: '#012D72',
            margin: '5px',
            border: '.25px solid #012D72',
            borderRadius: '4px'
        }
        // While loading permissions, render a hidden button to reserve layout and avoid flicker
        if (permissionsLoading) {
            return (
                <Box id={`cohort-attrition-funnel-${selectedCohort.id}`} key={`cohort-attrition-funnel-${selectedCohort.id}`}>
                    <span>
                        <IconButton size='small' sx={{ ...commonSx, visibility: 'hidden' }}>
                            <FilterAltOutlinedIcon />
                        </IconButton>
                    </span>
                </Box>
            )
        }
        if (permissions?.includes(PlatformPermissions.ACCESS_ATTRITION_FUNNEL)) {
            return (
                <Box id={`cohort-attrition-funnel-${selectedCohort.id}`} key={`cohort-attrition-funnel-${selectedCohort.id}`}>
                    {state.cohortAttritionFunnelLoading && (
                        <Box sx={{ margin: '5px', display: 'flex', alignItems: 'center' }}>
                            <LightTooltip title={t`Generating Attrition Funnel`} placement='top'>
                                <CircularProgress size={24} sx={{ margin: '11px' }} />
                            </LightTooltip>
                        </Box>
                    )}
                    {!state.cohortAttritionFunnelLoading && (
                        <LightTooltip title={t`Generate Attrition Funnel`} placement='top'>
                            <span>
                                <IconButton
                                    size='small'
                                    onClick={() => {
                                        if (selectedCohort.id) {
                                            actions.cohortAttritionFunnelGet({ cohortId: selectedCohort.id })
                                        }
                                    }}
                                    aria-label={t`Attrition Funnel`}
                                    sx={commonSx}
                                >
                                    <FilterAltOutlinedIcon />
                                </IconButton>
                            </span>
                        </LightTooltip>
                    )}
                </Box>
            )
        }
        return null
    }, [permissions, permissionsLoading, selectedCohort, state.cohortAttritionFunnelLoading, actions])

    const renderRefreshCountButton = useCallback(() => {
        if (!selectedCohort || selectedCohort.isSystem) return null
        const commonSx = {
            backgroundColor: '#D6E4F2',
            color: '#012D72',
            margin: '5px',
            border: '.25px solid #012D72',
            borderRadius: '4px'
        }
        if (permissionsLoading) {
            return (
                <Box id={`cohort-refresh-count-${selectedCohort.id}`} key={`cohort-refresh-count-${selectedCohort.id}`}>
                    <span>
                        <IconButton size='small' sx={{ ...commonSx, visibility: 'hidden' }}>
                            <RefreshOutlinedIcon />
                        </IconButton>
                    </span>
                </Box>
            )
        }
        if (permissions?.includes(PlatformPermissions.UPDATE_COHORT)) {
            return (
                <Box id={`cohort-refresh-count-${selectedCohort.id}`} key={`cohort-refresh-count-${selectedCohort.id}`}>
                    <LightTooltip title={t`Refresh Count`} placement='top'>
                        <span>
                            <IconButton
                                size='small'
                                onClick={() => handleRefreshCount(selectedCohort.id, selectedCohort.name)}
                                disabled={sizeLoading[selectedCohort.id]}
                                aria-label={t`Refresh Cohort Size`}
                                sx={commonSx}
                            >
                                <RefreshOutlinedIcon />
                            </IconButton>
                        </span>
                    </LightTooltip>
                </Box>
            )
        }
        return null
    }, [handleRefreshCount, selectedCohort, sizeLoading, permissions, permissionsLoading])

    const renderChatButton = useCallback(() => {
        if (!selectedCohort) return null
        if (permissionsLoading) {
            return (
                <Box id={`cohort-chat-dialog-${selectedCohort.id}`} key={`cohort-chat-dialog-${selectedCohort.id}`}>
                    <span>
                        <IconButton
                            key='chat'
                            size='small'
                            sx={{
                                background: 'linear-gradient(45deg, #012D72 30%, #8AB8E8 90%)',
                                color: '#012D72',
                                borderRadius: '4px',
                                margin: '5px',
                                border: '.25px solid #012D72',
                                visibility: 'hidden'
                            }}
                        >
                            <AutoAwesomeIcon sx={{ margin: '0.1rem', color: 'white', height: '22px', width: '22px' }} />
                        </IconButton>
                    </span>
                </Box>
            )
        }
        if (permissions?.includes(PlatformPermissions.ACCESS_ASSISTANT)) {
            return (
                <Box id={`cohort-chat-dialog-${selectedCohort.id}`} key={`cohort-chat-dialog-${selectedCohort.id}`}>
                    <LightTooltip key={'chatTooltip'} title={<Trans>Launch Assistant from this Cohort</Trans>} placement='top'>
                        <span>
                            <IconButton
                                key='chat'
                                color='primary'
                                size='small'
                                onClick={handleCreateConversation}
                                aria-label={t`Start Conversation from this Cohort`}
                                sx={{
                                    background: 'linear-gradient(45deg, #012D72 30%, #8AB8E8 90%)',
                                    color: '#012D72',
                                    borderRadius: '4px',
                                    margin: '5px',
                                    border: '.25px solid #012D72'
                                }}
                            >
                                <AutoAwesomeIcon sx={{ margin: '0.1rem', color: 'white', height: '22px', width: '22px' }} />
                            </IconButton>
                        </span>
                    </LightTooltip>
                </Box>
            )
        }
        return null
    }, [permissions, permissionsLoading, selectedCohort, handleCreateConversation])

    const cardActions = useMemo(() => {
        const actions: JSX.Element[] = []
        if (permissionsLoading) return actions
        if (selectedCohort !== undefined) {
            const refreshCountButton = renderRefreshCountButton()
            if (refreshCountButton && !selectedCohort.isFeatured) {
                actions.push(refreshCountButton)
            }

            const attritionFunnelButton = renderAttritionFunnelButton()
            if (attritionFunnelButton) {
                actions.push(attritionFunnelButton)
            }

            actions.push(
                <LightTooltip title={t`Edit Reports`} placement='top' key='edit-reports'>
                    <IconButton
                        key='distribution'
                        color='primary'
                        size='small'
                        component={Link}
                        to={toPath(Routes.COHORTBUILD) + `/${CohortRoutes.REPORTS}/${selectedCohort.id}/${reportType}`}
                        aria-label={t`Reports`}
                        sx={{
                            backgroundColor: '#D6E4F2',
                            color: '#012D72',
                            margin: '5px',
                            border: '.25px solid #012D72',
                            borderRadius: '4px'
                        }}
                    >
                        <BarChartIcon />
                    </IconButton>
                </LightTooltip>
            )

            actions.push(<GenerateReportComponent cohort={selectedCohort} key={`generate-report-${selectedCohort.id}`} />)

            if (!selectedCohort.isSystem && permissions?.includes(PlatformPermissions.ACCESS_DELIVERY)) {
                actions.push(
                    <LightTooltip title={t`Export to Snowflake`} placement='top' key='export-platform-cohort'>
                        <span>
                            <IconButton
                                key='export-platform-cohort'
                                color='primary'
                                size='small'
                                aria-label={t`Export to Snowflake`}
                                onClick={async () => {
                                    try {
                                        await ExplorerCohortsService.exportPlatformCohortExplorerCohortsCohortIdExportPlatformCohortPost({
                                            cohortId: selectedCohort.id
                                        })
                                        enqueueSnackbar(t`Cohort successfully exported to Snowflake (product.analytics.platform_cohorts table)`, {
                                            variant: 'success',
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
                                        })
                                    } catch (e) {
                                        enqueueSnackbar(t`Failed to export cohort`, {
                                            variant: 'error',
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
                                        })
                                    }
                                }}
                                sx={{
                                    backgroundColor: '#D6E4F2',
                                    color: '#012D72',
                                    margin: '5px',
                                    border: '.25px solid #012D72',
                                    borderRadius: '4px'
                                }}
                            >
                                <CloudUploadOutlinedIcon />
                            </IconButton>
                        </span>
                    </LightTooltip>
                )
            }

            if (!selectedCohort.isSystem && !selectedCohort.isFeatured && permissions?.includes(PlatformPermissions.UPDATE_COHORT)) {
                actions.push(
                    <LightTooltip title={t`Edit Cohort`} placement='top' key='edit-cohort'>
                        <IconButton
                            key='edit'
                            color='primary'
                            size='small'
                            component={Link}
                            to={toPath(Routes.COHORTBUILD) + `/custom/${selectedCohort.id}/edit`}
                            aria-label={t`Edit`}
                            sx={{
                                backgroundColor: '#D6E4F2',
                                color: '#012D72',
                                margin: '5px',
                                border: '.25px solid #012D72',
                                borderRadius: '4px'
                            }}
                            onClick={() =>
                                handleEditCohort({
                                    id: selectedCohort.id,
                                    name: selectedCohort.name,
                                    analyticsDataset: selectedCohort.analyticsDataset
                                })
                            }
                        >
                            <EditOutlinedIcon />
                        </IconButton>
                    </LightTooltip>
                )
            }

            const chatButton = renderChatButton()
            if (chatButton) {
                actions.push(chatButton)
            }
        }
        return actions
    }, [
        selectedCohort,
        permissions,
        permissionsLoading,
        handleEditCohort,
        reportType,
        renderRefreshCountButton,
        renderAttritionFunnelButton,
        renderChatButton
    ])

    const Card = useMemo(() => {
        const CardComponent = (props: { reportType: ReportAnalyticsType }) => {
            return (
                <CohortDashboardCohortCard
                    cohort={selectedCohort!}
                    actions={cardActions}
                    sizeLoading={selectedCohort && sizeLoading[selectedCohort.id]}
                />
            )
        }
        return CardComponent
    }, [selectedCohort, cardActions, sizeLoading])

    const GradientButton = styled(Button)(({ theme }) => ({
        background: 'linear-gradient(45deg, #012D72 30%, #8AB8E8 90%)',
        border: 0,
        boxShadow: '0 3px 5px 2px rgba(33, 203, 243, 0.3)',
        color: 'white',
        height: 48,
        padding: '0 30px',
        fontFamily: 'Metropolis,sans-serif',
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        transition: 'background 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(45deg, #001F4F 30%, #8AB8E8 90%)'
        },
        marginLeft: '10px'
    }))

    const BetaLabel = styled('span')(({ theme }) => ({
        backgroundColor: '#012D72',
        color: '#fff',
        borderRadius: '4px',
        fontSize: '0.7rem',
        padding: '2px 8px',
        marginLeft: theme.spacing(1)
    }))

    const cardRef = useRef<HTMLDivElement>(null)

    const CohortList = (
        <DashboardCohortList
            actions={actions}
            cohortList={sortedCohorts}
            cohortSizeLoading={state.cohortSizeLoading}
            datasetsList={sortedDatasets}
            selectedCohort={selectedCohort}
            searchValue={routing.queryParams.search}
            selectedOwner={cohortOwner}
            handleSearchChange={handleSearchChange}
            onCreateCohortClick={() => setCreateModalVisible(true)}
            onUpdateCohort={handleUpdateCohort}
            reportType={reportType ? reportType : ReportAnalyticsType.DEMOGRAPHICS}
            cohortsLoading={state.cohortsLoading}
            setCohortId={setCohortId}
            setCohortOwner={setCohortOwner}
            trackPage={actions.trackPage}
            page={page}
            setPage={setPage}
            totalPages={state.paginationMeta.totalPages}
        />
    )

    const detailsRef = useRef<HTMLDivElement>(null)

    const CohortDashboardDetailsPane = (
        <div key={`cohort-dashboard-details-pane`}>
            <Box position='relative'>
                <Box ref={cardRef}>
                    <Card reportType={reportType ?? ReportAnalyticsType.DEMOGRAPHICS} />
                </Box>
                <Box width='100%' flexDirection='column' marginBottom={1} sx={{ backgroundColor: '#F5F5F5' }} ref={detailsRef}>
                    {selectedCohort !== undefined && (
                        <DashboardCohortReports
                            cohortListItem={selectedCohort}
                            reportsActions={actions}
                            reportAnalyticsType={reportType || ReportAnalyticsType.DEMOGRAPHICS}
                            onReportAnalyticsTypeSelected={(reportAnalyticsType: ReportAnalyticsType) => {
                                setReportType(reportAnalyticsType)
                                const route = `/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${routing.pathParams.cohortOwner}/${selectedCohort.id}/${reportAnalyticsType}${location.search}`
                                actions.trackPage(route)
                                window.history.pushState({}, '', route)
                            }}
                        />
                    )}
                </Box>
            </Box>
        </div>
    )

    // Ensure report tab defaults to Demographics when cohort has no renderable query or is a system (Network) cohort
    useEffect(() => {
        if (!selectedCohort) return
        const hasRenderableQuery =
            !selectedCohort.isSystem &&
            !!(selectedCohort as any).query &&
            Array.isArray(((selectedCohort as any).query as any).filters) &&
            ((selectedCohort as any).query as any).filters.length > 0

        if ((selectedCohort.isSystem || !hasRenderableQuery) && reportType === ReportAnalyticsType.COHORT) {
            const newType = ReportAnalyticsType.DEMOGRAPHICS
            setReportType(newType)
            const route = `/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${routing.pathParams.cohortOwner}/${selectedCohort.id}/${newType}${location.search}`
            actions.trackPage(route)
            window.history.pushState({}, '', route)
        }
    }, [selectedCohort, reportType, actions, routing.pathParams.cohortOwner, location.search])

    const handleCreateEditDialogSave = (cohort: CohortSaveValues) => {
        actions.cohortCreate(cohort)
    }

    const [cardHeight, setCardHeight] = useState(cardRef.current?.clientHeight || 0)

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { height } = entry.contentRect
                setCardHeight(height)
            }
        })

        if (cardRef.current) {
            observer.observe(cardRef.current)
        }

        return () => {
            observer.disconnect()
        }
    }, [])

    const [reportsHeight, setReportsHeight] = useState(detailsRef?.current?.clientHeight || 0)

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { height } = entry.contentRect
                setReportsHeight(height)
            }
        })

        if (detailsRef.current) {
            observer.observe(detailsRef.current)
        }

        return () => {
            observer.disconnect()
        }
    }, [])

    // Capture the initial combined height of the card + reports the first time they are measured
    const initialLeftPaneHeightRef = useRef<number | null>(null)

    useEffect(() => {
        const combined = reportsHeight + cardHeight
        if (combined > 0 && initialLeftPaneHeightRef.current === null) {
            initialLeftPaneHeightRef.current = combined + 24
        }
    }, [reportsHeight, cardHeight])

    // Use the initially observed height to keep the left pane stable across tab/view changes
    const leftPaneHeight = useMemo(() => {
        if (initialLeftPaneHeightRef.current !== null) {
            return initialLeftPaneHeightRef.current
        }
        // Fallback before measurements complete
        return Math.round(window.innerHeight * 0.8)
    }, [initialLeftPaneHeightRef])

    const HeaderButtons = () => (
        <Box
            sx={{
                position: 'absolute',
                top: '8px',
                right: '24px',
                zIndex: 1000,
                display: 'flex',
                gap: 1,
                alignItems: 'center'
            }}
        >
            <Button
                variant='contained'
                color='primary'
                onClick={() => setCreateModalVisible(true)}
                startIcon={<AddIcon />}
                sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    height: 42.5,
                    padding: '0 15px',
                    fontSize: '14px'
                }}
            >
                <Trans>Create Custom Cohort</Trans>
            </Button>
            {permissions?.includes(PlatformPermissions.ACCESS_ASSISTANT) && (
                <GradientButton
                    onClick={() => {
                        actions.launchNewConversation({}, {})
                    }}
                    aria-label={t`AI Assistant`}
                    sx={{
                        height: 42.5,
                        borderRadius: '8px',
                        fontWeight: 500
                    }}
                >
                    <AutoAwesomeIcon sx={{ height: '18px', width: '16px', marginRight: '8px' }} />
                    <Typography sx={{ fontSize: '14px' }}>Assistant</Typography>
                    <BetaLabel>Beta</BetaLabel>
                </GradientButton>
            )}
        </Box>
    )

    return (
        <>
            <HeaderButtons />
            <Box display='flex' flexDirection='column' width={'100%'} overflow={'visible'} position='relative' paddingLeft='5px'>
                <>
                    <Box style={{ marginTop: '0px', display: '-webkit-inline-box' }}>
                        <Box
                            bgcolor='grey.200'
                            style={{
                                zIndex: '-1',
                                height: leftPaneHeight,
                                display: 'block',
                                padding: '5px',
                                width: '20dvw',
                                borderRadius: '8px',
                                border: '1px solid #E5E7EB',
                                overflow: 'hidden'
                            }}
                        >
                            {CohortList}
                        </Box>
                        <Box padding={1} sx={{ width: '79.25%' }}>
                            {cohortId && cohortId !== '-1' ? (
                                CohortDashboardDetailsPane
                            ) : (
                                <Box
                                    display='flex'
                                    flexDirection='column'
                                    // justifyContent="center"
                                    alignItems='center'
                                    height='100%'
                                    bgcolor='grey.100'
                                    borderRadius={2}
                                    padding={3}
                                >
                                    <Box fontSize='1.2rem' fontWeight='bold'>
                                        No Cohort Selected
                                    </Box>
                                    <Box fontSize='1rem' color='grey.600' mt={1}>
                                        Select a cohort to see data
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </>
                {createModalVisible && <WizardDialog onCancel={() => setCreateModalVisible(false)} onSave={handleCreateEditDialogSave} />}
            </Box>
        </>
    )
}
